import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useApp, api } from "@/context/AppContext";
import { Search } from "lucide-react";

export default function PublicStore() {
  const { slug } = useParams();
  const { lang, t } = useApp();
  const head = lang === "hi" ? "font-hi" : "font-display";
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const [search, setSearch] = useState("");
  const [fabric, setFabric] = useState("__all__");
  const [sort, setSort] = useState("newest");

  useEffect(() => {
    api.get(`/stores/${slug}`)
      .then((r) => setData(r.data))
      .catch(() => setErr(true));
  }, [slug]);

  const allFabrics = useMemo(() => {
    if (!data) return [];
    return Array.from(new Set(data.sarees.map((s) => s.fabric))).filter(Boolean);
  }, [data]);

  const filtered = useMemo(() => {
    if (!data) return [];
    let arr = [...data.sarees];
    if (fabric !== "__all__") arr = arr.filter((s) => s.fabric === fabric);
    if (search.trim()) {
      const q = search.toLowerCase();
      arr = arr.filter((s) => s.name.toLowerCase().includes(q) || (s.description || "").toLowerCase().includes(q));
    }
    if (sort === "low") arr.sort((a, b) => a.price - b.price);
    else if (sort === "high") arr.sort((a, b) => b.price - a.price);
    return arr;
  }, [data, fabric, sort, search]);

  if (err) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-24 text-center">
        <div className="text-3xl">🥻</div>
        <div className={`${head} text-2xl mt-4 text-[#2D1820]`}>Store not found</div>
        <Link to="/" className="btn-secondary mt-6 inline-block">Go home</Link>
      </div>
    );
  }
  if (!data) return <div className="max-w-7xl mx-auto px-4 py-24 text-center text-[#635057]">{t.common.loading}</div>;

  const { store, sarees } = data;

  return (
    <div data-testid="public-store-page">
      <section className="border-b border-[#E8DFDC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-10 lg:py-16">
          <div className="text-xs tracking-[0.3em] uppercase font-semibold text-[#C9A84C]">✦ {t.catalog.welcome}</div>
          <h1 className={`${head} text-4xl sm:text-5xl lg:text-6xl text-[#6B1F3A] font-light tracking-tight mt-3`} data-testid="store-name">
            {store.store_name}
          </h1>
          <p className="mt-4 text-[#635057] max-w-xl">{t.catalog.sub}</p>
        </div>
      </section>

      {/* Filter bar */}
      {sarees.length > 0 && (
        <section className="border-b border-[#E8DFDC] bg-[#FCFAF8] sticky top-16 z-30">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-4 flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[220px] max-w-md">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#635057]" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t.catalog.search}
                className="field !py-2 !pl-9 !bg-white"
                data-testid="catalog-search"
              />
            </div>
            <div className="flex items-center gap-2 overflow-x-auto -mx-1 px-1" data-testid="catalog-fabric-filters">
              <Chip active={fabric === "__all__"} onClick={() => setFabric("__all__")} testId="fabric-all">{t.catalog.filterAll}</Chip>
              {allFabrics.map((f) => (
                <Chip key={f} active={fabric === f} onClick={() => setFabric(f)} testId={`fabric-${f}`}>{f}</Chip>
              ))}
            </div>
            <select value={sort} onChange={(e) => setSort(e.target.value)} className="field !py-2 !w-auto !bg-white text-xs uppercase tracking-widest" data-testid="catalog-sort">
              <option value="newest">{t.catalog.sortNewest}</option>
              <option value="low">{t.catalog.sortLowHigh}</option>
              <option value="high">{t.catalog.sortHighLow}</option>
            </select>
          </div>
        </section>
      )}

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-12 lg:py-16">
        {sarees.length === 0 ? (
          <div className="text-center text-[#635057] py-16">{t.catalog.empty}</div>
        ) : filtered.length === 0 ? (
          <div className="text-center text-[#635057] py-16" data-testid="catalog-no-match">{t.catalog.noMatch}</div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 sm:gap-8">
            {filtered.map((s) => (
              <Link key={s.id} to={`/tryon/${s.id}`} className="card-product group" data-testid={`store-saree-${s.id}`}>
                <div className="aspect-[3/4] overflow-hidden bg-[#FCFAF8]">
                  <img src={s.image_url} alt={s.name} className="w-full h-full object-cover" loading="lazy" />
                </div>
                <div className="p-4 sm:p-5">
                  <div className="text-[#2D1820] font-medium text-sm sm:text-base">{s.name}</div>
                  <div className="text-xs text-[#635057] mt-1">{s.fabric}</div>
                  <div className="flex items-baseline justify-between mt-3">
                    <div className="text-[#6B1F3A] font-semibold">₹ {Number(s.price).toLocaleString("en-IN")}</div>
                    <div className="text-[10px] tracking-[0.2em] uppercase text-[#C9A84C] group-hover:text-[#6B1F3A] transition-colors">
                      {t.catalog.tryon} →
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function Chip({ active, onClick, children, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`text-[11px] tracking-[0.18em] uppercase font-semibold px-4 py-2 border whitespace-nowrap transition-colors ${
        active
          ? "bg-[#6B1F3A] text-white border-[#6B1F3A]"
          : "bg-white text-[#635057] border-[#E8DFDC] hover:border-[#C9A84C] hover:text-[#6B1F3A]"
      }`}
    >
      {children}
    </button>
  );
}
